import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.*
import models.DomainGame
import models.GameAnalytics
import models.GameAnalyticsCalculator
import models.GameAward
import models.GameAwardKind
import models.PlayStyle
import models.PlayerAnalytics
import models.RoundModifier
import models.RoundRecap
import org.jetbrains.compose.resources.stringResource

/**
 * The full analytics view for one game — live while it's being played (opened from the game's top
 * bar) and final on the game-over screen. It re-derives everything from the synced game on every
 * update, so it ticks forward as dice land.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun GameStatsSheet(
    game: DomainGame,
    selfName: String,
    onDismiss: () -> Unit,
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = onDismiss,
        sheetState = sheetState,
    ) {
        GameStatsContent(
            game = game,
            selfName = selfName,
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .navigationBarsPadding()
                .padding(horizontal = 16.dp)
                .padding(bottom = 24.dp),
        )
    }
}

@Composable
internal fun GameStatsContent(
    game: DomainGame,
    selfName: String,
    modifier: Modifier = Modifier,
) {
    val analytics = remember(game) { GameAnalyticsCalculator.from(game) }
    val tableIndexByName = remember(analytics) { analytics.players.associate { it.playerName to it.tableIndex } }
    val round = game.round
    val isOver = game.finished || (round != null && round.roundNum > game.endRoundNum)

    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = stringResource(Res.string.game_stats_title),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = if (isOver || round == null) {
                    stringResource(Res.string.game_stats_final_subtitle, game.endRoundNum.coerceAtLeast(analytics.totals.roundsCompleted))
                } else {
                    stringResource(Res.string.game_stats_live_subtitle, round.roundNum, game.endRoundNum)
                },
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = stringResource(
                    Res.string.table_totals,
                    analytics.totals.rolls,
                    analytics.totals.banks,
                    analytics.totals.busts,
                    analytics.totals.averagePeakPot?.toString() ?: stringResource(Res.string.stats_dash),
                ),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }

        StandingsCard(analytics, tableIndexByName, selfName)

        StatsCard {
            StatsSectionTitle(stringResource(Res.string.stats_section_score_race))
            val race = analytics.scoreRace
            when {
                race != null -> ScoreRaceChart(race = race, selfName = selfName)
                !analytics.historyComplete -> BodyNote(stringResource(Res.string.stats_history_incomplete))
                else -> BodyNote(stringResource(Res.string.stats_no_rounds_yet))
            }
        }

        if (analytics.awards.isNotEmpty()) {
            StatsCard {
                StatsSectionTitle(stringResource(Res.string.stats_section_awards))
                analytics.awards.forEach { award -> AwardRow(award) }
            }
        }

        StatsCard {
            StatsSectionTitle(stringResource(Res.string.stats_section_players))
            analytics.players.forEachIndexed { index, player ->
                if (index > 0) HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                PlayerBreakdown(player = player, isSelf = player.playerName == selfName)
            }
        }

        analytics.rollDistribution?.let { distribution ->
            StatsCard {
                StatsSectionTitle(stringResource(Res.string.stats_section_dice))
                BodyNote(
                    stringResource(
                        Res.string.dice_report_sevens,
                        formatPercent(distribution.shareFor(7)),
                        formatPercent(distribution.expectedShareFor(7)),
                    ),
                )
                RollDistributionChart(distribution = distribution)
            }
        }

        if (analytics.rounds.isNotEmpty()) {
            StatsCard {
                StatsSectionTitle(stringResource(Res.string.stats_section_rounds))
                analytics.rounds.forEachIndexed { index, recap ->
                    if (index > 0) HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    RoundHistoryRow(recap)
                }
            }
        }
    }
}

@Composable
private fun StandingsCard(
    analytics: GameAnalytics,
    tableIndexByName: Map<String, Int>,
    selfName: String,
) {
    StatsCard {
        StatsSectionTitle(stringResource(Res.string.stats_section_standings))
        analytics.standings.forEach { row ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(
                    text = row.rank.toString(),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.widthIn(min = 24.dp),
                )
                SeriesDot(seriesColor(tableIndexByName[row.playerName] ?: Int.MAX_VALUE))
                Spacer(modifier = Modifier.width(8.dp))
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = row.playerName,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = if (row.playerName == selfName) FontWeight.Bold else FontWeight.Medium,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                    val tag = when {
                        row.playerName == selfName -> stringResource(Res.string.stats_you)
                        row.isBot -> stringResource(Res.string.stats_bot)
                        else -> null
                    }
                    if (tag != null) {
                        Text(
                            text = tag,
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
                Column(horizontalAlignment = Alignment.End) {
                    Text(
                        text = stringResource(Res.string.points_short_format, row.points),
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                    )
                    Text(
                        text = if (row.pointsBehindLeader == 0) {
                            stringResource(Res.string.stats_leader)
                        } else {
                            stringResource(Res.string.stats_points_behind, row.pointsBehindLeader)
                        },
                        style = MaterialTheme.typography.labelSmall,
                        color = if (row.pointsBehindLeader == 0) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun AwardRow(award: GameAward) {
    val (emoji, title, detail) = when (award.kind) {
        GameAwardKind.IRON_NERVES -> Triple(
            "🧊",
            stringResource(Res.string.award_iron_nerves),
            stringResource(Res.string.award_iron_nerves_detail, formatOneDecimal(award.value / 10.0)),
        )
        GameAwardKind.SAFE_HANDS -> Triple(
            "🛟",
            stringResource(Res.string.award_safe_hands),
            stringResource(Res.string.award_safe_hands_detail, formatOneDecimal(award.value / 10.0)),
        )
        GameAwardKind.DOUBLES_DEALER -> Triple(
            "🎲",
            stringResource(Res.string.award_doubles_dealer),
            stringResource(Res.string.award_doubles_dealer_detail, award.value),
        )
        GameAwardKind.BUST_MAGNET -> Triple(
            "💥",
            stringResource(Res.string.award_bust_magnet),
            stringResource(Res.string.award_bust_magnet_detail, award.value),
        )
        GameAwardKind.ESCAPE_ARTIST -> Triple(
            "🏃",
            stringResource(Res.string.award_escape_artist),
            stringResource(Res.string.award_escape_artist_detail, award.value),
        )
    }
    Row(verticalAlignment = Alignment.CenterVertically) {
        Text(text = emoji, style = MaterialTheme.typography.titleLarge)
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = "$title · ${award.playerNames.joinToString(" & ")}",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = detail,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun PlayerBreakdown(player: PlayerAnalytics, isSelf: Boolean) {
    val dash = stringResource(Res.string.stats_dash)
    val stats = player.stats
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            SeriesDot(seriesColor(player.tableIndex))
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = if (isSelf) "${player.playerName} · ${stringResource(Res.string.stats_you)}" else player.playerName,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            player.style?.let { style -> StyleChip(style) }
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = stringResource(Res.string.points_short_format, player.points),
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
        Row(modifier = Modifier.fillMaxWidth()) {
            MiniStat(stringResource(Res.string.stats_banks), stats.banks.toString(), Modifier.weight(1f))
            MiniStat(stringResource(Res.string.stats_avg_bank), player.averageBank?.toString() ?: dash, Modifier.weight(1f))
            MiniStat(stringResource(Res.string.stats_best_bank), if (stats.banks > 0) stats.bestBank.toString() else dash, Modifier.weight(1f))
            MiniStat(
                stringResource(Res.string.stats_nerve),
                player.averageRollsBeforeBank?.let { stringResource(Res.string.stats_nerve_value, formatOneDecimal(it)) } ?: dash,
                Modifier.weight(1f),
            )
        }
        Row(modifier = Modifier.fillMaxWidth()) {
            MiniStat(stringResource(Res.string.stats_rolls), stats.rolls.toString(), Modifier.weight(1f))
            MiniStat(stringResource(Res.string.stats_sevens), stats.sevens.toString(), Modifier.weight(1f))
            MiniStat(stringResource(Res.string.stats_doubles), stats.doubles.toString(), Modifier.weight(1f))
            MiniStat(
                stringResource(Res.string.stats_busted),
                stringResource(Res.string.stats_busted_value, stats.timesBusted),
                Modifier.weight(1f),
            )
        }
    }
}

@Composable
internal fun StyleChip(style: PlayStyle) {
    Surface(
        shape = RoundedCornerShape(percent = 50),
        color = MaterialTheme.colorScheme.secondaryContainer,
        contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
    ) {
        Text(
            text = playStyleLabel(style),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
        )
    }
}

@Composable
internal fun playStyleLabel(style: PlayStyle): String = when (style) {
    PlayStyle.CAUTIOUS -> stringResource(Res.string.style_cautious)
    PlayStyle.BALANCED -> stringResource(Res.string.style_balanced)
    PlayStyle.DAREDEVIL -> stringResource(Res.string.style_daredevil)
}

@Composable
private fun MiniStat(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        Text(
            text = value,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun RoundHistoryRow(recap: RoundRecap) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Surface(
            shape = RoundedCornerShape(10.dp),
            color = MaterialTheme.colorScheme.surfaceContainerHighest,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        ) {
            Text(
                text = stringResource(Res.string.stats_round_short, recap.roundNum),
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier
                    .widthIn(min = 40.dp)
                    .padding(horizontal = 8.dp, vertical = 6.dp),
            )
        }
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            val outcome = when {
                recap.busted && recap.bustedBy.isNotBlank() -> stringResource(Res.string.round_ended_bust, recap.bustedBy)
                recap.busted -> stringResource(Res.string.round_ended_bust_unknown)
                recap.completed -> stringResource(Res.string.round_ended_banked)
                else -> stringResource(Res.string.round_in_progress)
            }
            val twist = if (recap.modifier != RoundModifier.NONE) roundModifierTitle(recap.modifier) else null
            Text(
                text = if (twist != null) "$outcome · $twist" else outcome,
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            val topGain = recap.scoreChanges.maxByOrNull { it.points }?.takeIf { it.points > 0 }
            val meta = buildList {
                add(stringResource(Res.string.round_peak_pot, recap.peakPot))
                add(stringResource(Res.string.round_rolls, recap.rolls))
                if (topGain != null) add(stringResource(Res.string.round_top_gain, topGain.playerName, topGain.points))
            }.joinToString(" · ")
            Text(
                text = meta,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun BodyNote(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
}
